// Import utilities from shared services

import UserService from '@/services/user';
import { useAppStore } from '@/stores/useAppStore';
import { logger } from '@/utils/logger';
import {
  getAllSettings,
  updateSetting as apiUpdateSetting,
  batchUpdateSettings as apiBatchUpdateSettings,
} from '@shared/services/apiFeatures';

type SettingsChangeListener = (key: string, value: string) => void;

const settingsChangeListeners: SettingsChangeListener[] = [];

/**
 * Registers a listener that is notified whenever a setting is changed.
 *
 * @param listener - Callback invoked with the setting key and new value on each change.
 */
export const addSettingsChangeListener = (listener: SettingsChangeListener): void => {
  settingsChangeListeners.push(listener);
};

const notifySettingsChange = (key: string, value: string): void => {
  settingsChangeListeners.forEach(listener => listener(key, value));
};

interface Setting {
  key: string;
  value: string;
  description?: string;
  updated_at?: string;
}

/**
 * Service for managing application settings with persona and theme integration.
 *
 * Provides methods to load, update, and batch-update settings. Integrates with
 * the user profile for theme_mode and persona fields (preferred_name, location,
 * timezone, receive_daily_brief). Notifies registered listeners on every change.
 */
class SettingsService {
  /**
   * Fetches all raw settings from the API.
   *
   * @returns A promise resolving to the array of Setting objects from the server.
   */
  async getAllSettings(): Promise<Setting[]> {
    return getAllSettings();
  }

  /** Merge persona fields into the settings map. */
  private applyPersonaToMap(
    persona: {
      preferred_name?: string;
      location?: string;
      timezone?: string;
      receive_daily_brief?: boolean;
    },
    map: Record<string, string>
  ): void {
    if (persona.preferred_name) map.preferred_name = persona.preferred_name;
    if (persona.location) map.location = persona.location;
    if (persona.timezone) map.timezone = persona.timezone;
    if (persona.receive_daily_brief !== undefined) {
      map.receive_daily_brief = String(persona.receive_daily_brief);
    }
  }

  /** Load persona from store, fetching if needed. */
  private async loadPersona(): Promise<
    | {
        preferred_name?: string;
        location?: string;
        timezone?: string;
        receive_daily_brief?: boolean;
      }
    | undefined
  > {
    const state = useAppStore.getState() as unknown as Record<string, unknown>;
    const currentPersona = state.currentPersona as
      | {
          preferred_name?: string;
          location?: string;
          timezone?: string;
          receive_daily_brief?: boolean;
        }
      | undefined;
    const loadCurrentPersona = state.loadCurrentPersona as
      | (() => Promise<typeof currentPersona>)
      | undefined;
    if (currentPersona) return currentPersona;
    if (loadCurrentPersona) return loadCurrentPersona();
    return undefined;
  }

  /**
   * Loads all settings into a key-value map, merging data from three sources.
   *
   * Fetches settings from the API, then enriches the map with the user's
   * theme_mode preference and persona fields (preferred_name, location,
   * timezone, receive_daily_brief). Gracefully handles failures from user
   * or persona data loading.
   *
   * @param knownUser - The user the caller just fetched. Boot and login already
   *   hold it, so passing it saves a second `/api/auth/me`; omit it to fetch.
   * @returns A promise resolving to a record mapping setting keys to their string values.
   * @throws If the core settings API call fails.
   */
  async loadAllSettings(knownUser?: { theme_mode?: string }): Promise<Record<string, string>> {
    try {
      // Fired together, not in sequence. The three sources are independent, and this
      // chain sat on the critical path of the app's first paint - three round trips
      // deep when it only ever needed to be one.
      const [settingsResult, userResult, personaResult] = await Promise.allSettled([
        getAllSettings(),
        knownUser ?? UserService.getCurrentUser(),
        this.loadPersona(),
      ]);

      // Settings are the one hard requirement; user and persona only enrich the map.
      if (settingsResult.status === 'rejected') throw settingsResult.reason;

      const map: Record<string, string> = {};
      settingsResult.value.forEach(s => {
        map[s.key] = s.value;
      });

      if (userResult.status === 'fulfilled') {
        if (userResult.value.theme_mode) map.theme_mode = userResult.value.theme_mode;
      } else {
        logger.warn('Could not load user data for theme_mode:', userResult.reason);
      }

      if (personaResult.status === 'fulfilled') {
        if (personaResult.value) this.applyPersonaToMap(personaResult.value, map);
      } else {
        logger.warn('Could not load persona data for settings:', personaResult.reason);
      }

      return map;
    } catch (error) {
      logger.error('Error loading all settings:', error);
      throw error;
    }
  }

  /**
   * Updates a single setting by key, routing to the appropriate handler.
   *
   * Routes theme_mode updates through updateThemeMode, persona-related keys
   * through updatePersonaSetting, and all others through the standard API.
   * Notifies change listeners after a successful update.
   *
   * @param key - The setting key to update.
   * @param value - The new setting value.
   * @param description - Optional description for the setting.
   * @returns A promise resolving to the updated Setting object.
   */
  async updateSetting(
    key: string,
    value: string,
    description: string | null = null
  ): Promise<Setting> {
    if (key === 'theme_mode') {
      return this.updateThemeMode(value, description);
    }
    if (
      [
        'receive_daily_brief',
        'preferred_name',
        'location',
        'timezone',
        'default_send_as_email',
      ].includes(key)
    ) {
      return this.updatePersonaSetting(key, value, description);
    }

    const result = await apiUpdateSetting(key, value, description);
    notifySettingsChange(key, value);
    return result;
  }

  /**
   * Updates the theme_mode setting and notifies change listeners.
   *
   * @param value - The new theme mode value (e.g., "light", "dark", "system").
   * @param description - Optional description for the setting.
   * @returns A promise resolving to the updated Setting object.
   */
  async updateThemeMode(value: string, description: string | null = null): Promise<Setting> {
    const result = await apiUpdateSetting('theme_mode', value, description);
    notifySettingsChange('theme_mode', value);
    return result;
  }

  /**
   * Updates a persona-related setting and notifies change listeners.
   *
   * Handles keys like preferred_name, location, timezone, receive_daily_brief,
   * and default_send_as_email.
   *
   * @param key - The persona setting key to update.
   * @param value - The new value for the setting.
   * @param description - Optional description for the setting.
   * @returns A promise resolving to the updated Setting object.
   */
  async updatePersonaSetting(
    key: string,
    value: string,
    description: string | null = null
  ): Promise<Setting> {
    const result = await apiUpdateSetting(key, value, description);
    notifySettingsChange(key, value);
    return result;
  }

  /**
   * Updates multiple settings in a single batch API call.
   *
   * Sends all settings to the batch update endpoint and then notifies
   * change listeners for each individual setting.
   *
   * @param settings - Array of setting objects, each with key, value, and optional description.
   * @returns A promise resolving to the array of updated Setting objects.
   */
  async batchUpdateSettings(
    settings: { key: string; value: string; description?: string | null }[]
  ): Promise<Setting[]> {
    const results = await apiBatchUpdateSettings(settings);

    for (const item of settings) {
      notifySettingsChange(item.key, item.value);
    }

    return results;
  }

  /**
   * Updates the assistant's preferred name setting.
   *
   * Convenience method that delegates to updateSetting with the 'preferred_name' key.
   *
   * @param name - The new preferred name for the assistant.
   * @returns A promise resolving to the updated Setting object.
   */
  async updateAssistantName(name: string): Promise<Setting> {
    return this.updateSetting('preferred_name', name);
  }
}

export default new SettingsService();
